import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "components/headers/HeaderLight";
import { SectionHeading } from "components/misc/Headings.js";

const Container = tw.div`relative min-h-screen bg-gradient-to-br from-blue-500 to-blue-100`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-3xl sm:text-4xl lg:text-5xl text-center leading-tight text-gray-800`;

// Main layout containers
const MainContent = tw.div`flex flex-col lg:flex-row gap-8 mt-12 px-4 lg:px-8`;
const LeftSection = tw.div`flex-1 flex flex-col items-center justify-center`;
const RightSection = tw.div`flex-1 flex flex-col items-center justify-center`;

// Access methods container
const AccessMethodsContainer = tw.div`bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-8`;
const AccessMethodsTitle = tw.h3`text-2xl font-bold text-gray-800 mb-8 text-center`;
const AccessMethodsGrid = tw.div`grid grid-cols-1 md:grid-cols-3 gap-6`;

// Individual access method cards
const AccessMethodCard = styled.div(({ isActive }) => [
  tw`bg-gray-100 border-2 border-gray-200 rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-lg text-center`,
  isActive && tw`border-blue-500 bg-blue-100 shadow-lg transform scale-105`
]);

const AccessIcon = tw.div`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center text-4xl bg-white shadow-md`;
const AccessTitle = tw.h4`text-lg font-bold text-gray-800 mb-2`;
const AccessDescription = tw.p`text-gray-600 text-sm`;

// User display section
const UserDisplayContainer = tw.div`bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-8`;
const UserImageContainer = styled.div(({ accessStatus }) => [
  tw`w-64 h-64 mx-auto mb-6 rounded-2xl overflow-hidden border-8 transition-all duration-500 shadow-xl`,
  accessStatus === 'granted' && tw`border-green-400 shadow-green-200`,
  accessStatus === 'denied' && tw`border-red-400 shadow-red-200`,
  accessStatus === 'waiting' && tw`border-gray-300 shadow-gray-200`
]);

const UserImage = tw.img`w-full h-full object-cover`;
const PlaceholderImage = tw.div`w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-6xl text-white`;

// Status display
const StatusContainer = tw.div`text-center mt-6`;
const StatusLabel = styled.span(({ accessStatus }) => [
  tw`text-4xl font-black uppercase tracking-wider py-3 px-8 rounded-2xl inline-block transition-all duration-500`,
  accessStatus === 'granted' && tw`text-green-700 bg-green-100 shadow-lg`,
  accessStatus === 'denied' && tw`text-red-700 bg-red-100 shadow-lg`,
  accessStatus === 'waiting' && tw`text-gray-700 bg-gray-100 shadow-lg`
]);

// Keypad for numerical input
const KeypadContainer = tw.div`mt-6`;
const KeypadGrid = tw.div`grid grid-cols-3 gap-3 max-w-xs mx-auto`;
const KeypadButton = tw.button`bg-white border-2 border-gray-300 rounded-xl p-4 text-xl font-bold hover:bg-gray-100 hover:border-blue-400 transition-all duration-200 shadow-md`;
const KeypadDisplay = tw.div`bg-gray-100 border-2 border-gray-300 rounded-xl p-4 text-center text-2xl font-mono mb-4 min-h-16 flex items-center justify-center`;

// Authentication input areas
const AuthInput = tw.div`mt-4 p-4 bg-blue-100 border-2 border-blue-200 rounded-xl text-center`;
const AuthButton = tw.button`mt-4 bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition-all duration-200 shadow-lg`;

// Recent access log
const RecentAccessContainer = tw.div`mt-12 bg-white backdrop-blur-md border border-blue-300 shadow-2xl rounded-3xl p-6`;
const RecentAccessTitle = tw.h3`text-2xl font-bold text-gray-800 mb-6 text-center`;
const AccessLogItem = tw.div`flex items-center justify-between py-3 border-b border-gray-200 last:border-b-0`;
const AccessLogText = tw.p`text-gray-700`;
const AccessLogTime = tw.span`text-gray-500 text-sm`;
const AccessLogStatus = styled.span(({ status }) => [
  tw`px-3 py-1 rounded-full text-xs font-bold ml-3`,
  status === 'granted' && tw`bg-green-100 text-green-800`,
  status === 'denied' && tw`bg-red-100 text-red-800`
]);

const AccessDoorPage = () => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [accessStatus, setAccessStatus] = useState('waiting');
  const [currentUser, setCurrentUser] = useState(null);
  const [keypadInput, setKeypadInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock user data
  const mockUsers = [
    {
      id: 1,
      name: "María González",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612f2db?w=400&h=400&fit=crop&crop=face",
      house: "Casa #203",
      accessCode: "1234",
      hasFingerprint: true,
      hasCard: true
    },
    {
      id: 2,
      name: "Carlos Rodríguez",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      house: "Casa #105",
      accessCode: "5678",
      hasFingerprint: true,
      hasCard: false
    },
    {
      id: 3,
      name: "Ana Martínez",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      house: "Casa #78",
      accessCode: "9999",
      hasFingerprint: false,
      hasCard: true
    }
  ];

  // Recent access log data
  const [recentAccess] = useState([
    { user: "María González", house: "Casa #203", time: "10:30 AM", status: "granted", method: "Huella Digital" },
    { user: "Carlos Rodríguez", house: "Casa #105", time: "09:45 AM", status: "granted", method: "Código" },
    { user: "Usuario Desconocido", house: "N/A", time: "09:15 AM", status: "denied", method: "Tarjeta" },
    { user: "Ana Martínez", house: "Casa #78", time: "08:30 AM", status: "granted", method: "Tarjeta" },
    { user: "Usuario Desconocido", house: "N/A", time: "07:20 AM", status: "denied", method: "Código" }
  ]);

  const accessMethods = [
    {
      id: 'fingerprint',
      title: 'Huella Digital',
      description: 'Escanear huella dactilar',
      icon: '👆',
      color: 'blue'
    },
    {
      id: 'keypad',
      title: 'Código Numérico',
      description: 'Ingresar código de acceso',
      icon: '🔢',
      color: 'green'
    },
    {
      id: 'card',
      title: 'Tarjeta/QR',
      description: 'Escanear tarjeta o código QR',
      icon: '💳',
      color: 'purple'
    }
  ];

  // Simulate access attempt
  const simulateAccess = () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setAccessStatus('waiting');
    
    // Simulate processing delay
    setTimeout(() => {
      // Randomly select a user or simulate unknown user
      const randomUser = Math.random() > 0.3 ? mockUsers[Math.floor(Math.random() * mockUsers.length)] : null;
      
      if (randomUser) {
        setCurrentUser(randomUser);
        
        // Check if user has access with selected method
        let hasAccess = false;
        
        switch (selectedMethod) {
          case 'fingerprint':
            hasAccess = randomUser.hasFingerprint;
            break;
          case 'keypad':
            hasAccess = keypadInput === randomUser.accessCode;
            break;
          case 'card':
            hasAccess = randomUser.hasCard;
            break;
          default:
            hasAccess = false;
        }
        
        setAccessStatus(hasAccess ? 'granted' : 'denied');
      } else {
        // Unknown user
        setCurrentUser({
          name: "Usuario Desconocido",
          image: null,
          house: "N/A"
        });
        setAccessStatus('denied');
      }
      
      setIsProcessing(false);
      
      // Reset after 5 seconds
      setTimeout(() => {
        setAccessStatus('waiting');
        setCurrentUser(null);
        setKeypadInput('');
        setSelectedMethod(null);
      }, 5000);
    }, 2000);
  };

  const handleKeypadPress = (digit) => {
    if (keypadInput.length < 4) {
      setKeypadInput(keypadInput + digit);
    }
  };

  const clearKeypad = () => {
    setKeypadInput('');
  };

  const handleMethodSelect = (methodId) => {
    setSelectedMethod(methodId);
    if (methodId !== 'keypad') {
      setKeypadInput('');
    }
  };

  const getStatusText = () => {
    if (isProcessing) return 'PROCESANDO...';
    switch (accessStatus) {
      case 'granted': return 'ACCESO PERMITIDO';
      case 'denied': return 'ACCESO DENEGADO';
      default: return 'ESPERANDO ACCESO';
    }
  };

  const keypadNumbers = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['C', '0', '✓']
  ];

  return (
    <AnimationRevealPage>
      <Header />
      <Container className="py-12">
        <Heading>Control de Acceso</Heading>
        <p className="text-center text-gray-600 mt-4 mb-8">
          Sistema de monitoreo y control de acceso a la puerta principal
        </p>

        <MainContent>
          {/* Left Section - Access Methods */}
          <LeftSection>
            <AccessMethodsContainer>
              <AccessMethodsTitle>Métodos de Acceso</AccessMethodsTitle>
              <AccessMethodsGrid>
                {accessMethods.map((method) => (
                  <AccessMethodCard
                    key={method.id}
                    isActive={selectedMethod === method.id}
                    onClick={() => handleMethodSelect(method.id)}
                  >
                    <AccessIcon>
                      {method.icon}
                    </AccessIcon>
                    <AccessTitle>{method.title}</AccessTitle>
                    <AccessDescription>{method.description}</AccessDescription>
                  </AccessMethodCard>
                ))}
              </AccessMethodsGrid>

              {/* Method-specific inputs */}
              {selectedMethod === 'fingerprint' && (
                <AuthInput>
                  <p className="text-blue-700 font-semibold mb-2">Coloque su dedo en el escáner</p>
                  <div className="text-6xl">👆</div>
                  <AuthButton onClick={simulateAccess} disabled={isProcessing}>
                    {isProcessing ? 'Escaneando...' : 'Escanear Huella'}
                  </AuthButton>
                </AuthInput>
              )}

              {selectedMethod === 'keypad' && (
                <div>
                  <AuthInput>
                    <p className="text-green-700 font-semibold mb-2">Ingrese su código de 4 dígitos</p>
                    <KeypadDisplay>
                      {keypadInput.replace(/./g, '●') || 'Ingrese código...'}
                    </KeypadDisplay>
                  </AuthInput>
                  <KeypadContainer>
                    <KeypadGrid>
                      {keypadNumbers.flat().map((num) => (
                        <KeypadButton
                          key={num}
                          onClick={() => {
                            if (num === 'C') clearKeypad();
                            else if (num === '✓') simulateAccess();
                            else handleKeypadPress(num);
                          }}
                          disabled={isProcessing}
                        >
                          {num}
                        </KeypadButton>
                      ))}
                    </KeypadGrid>
                  </KeypadContainer>
                </div>
              )}

              {selectedMethod === 'card' && (
                <AuthInput>
                  <p className="text-purple-700 font-semibold mb-2">Acerque su tarjeta o código QR</p>
                  <div className="text-6xl">💳</div>
                  <AuthButton onClick={simulateAccess} disabled={isProcessing}>
                    {isProcessing ? 'Leyendo...' : 'Escanear Tarjeta/QR'}
                  </AuthButton>
                </AuthInput>
              )}
            </AccessMethodsContainer>
          </LeftSection>

          {/* Right Section - User Display */}
          <RightSection>
            <UserDisplayContainer>
              <UserImageContainer accessStatus={accessStatus}>
                {currentUser?.image ? (
                  <UserImage src={currentUser.image} alt={currentUser.name} />
                ) : (
                  <PlaceholderImage>
                    {isProcessing ? '⏳' : currentUser ? '❓' : '👤'}
                  </PlaceholderImage>
                )}
              </UserImageContainer>

              <StatusContainer>
                <StatusLabel accessStatus={accessStatus}>
                  {getStatusText()}
                </StatusLabel>
                
                {currentUser && (
                  <div className="mt-4">
                    <p className="text-xl font-bold text-gray-800">{currentUser.name}</p>
                    <p className="text-gray-600">{currentUser.house}</p>
                  </div>
                )}
              </StatusContainer>
            </UserDisplayContainer>
          </RightSection>
        </MainContent>

        {/* Recent Access Log */}
        <RecentAccessContainer>
          <RecentAccessTitle>Registro de Accesos Recientes</RecentAccessTitle>
          <div>
            {recentAccess.map((access, index) => (
              <AccessLogItem key={index}>
                <div>
                  <AccessLogText>
                    <strong>{access.user}</strong> {access.house !== 'N/A' && `- ${access.house}`}
                    <span className="text-gray-500"> • {access.method}</span>
                    <AccessLogStatus status={access.status}>
                      {access.status === 'granted' ? 'Permitido' : 'Denegado'}
                    </AccessLogStatus>
                  </AccessLogText>
                </div>
                <AccessLogTime>{access.time}</AccessLogTime>
              </AccessLogItem>
            ))}
          </div>
        </RecentAccessContainer>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};

export default AccessDoorPage;